/**
 * Offline outbox — IndexedDB-backed queue of POST operations that couldn't
 * reach the server. Auto-synced when connectivity returns.
 *
 * Strategy for v1 (per UX decisions):
 *  - Supports offline inward + outward transaction creation only
 *  - Uses a temporary client-side id (OFFLINE-...) until the server assigns
 *    the real transaction_id on sync. The returned transaction_id is mapped
 *    back to the tmp id so the UI can update optimistic rows.
 */
import { openDB } from 'idb';

const DB_NAME = 'steelflow-offline';
const DB_VERSION = 1;
const STORE_OPS = 'pending-ops';

let dbPromise = null;
const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_OPS)) {
          const store = db.createObjectStore(STORE_OPS, { keyPath: 'tmp_id' });
          store.createIndex('byStatus', 'status');
          store.createIndex('byCreatedAt', 'created_at');
        }
      },
    });
  }
  return dbPromise;
};

export const OP_TYPES = {
  INWARD: 'inward',
  OUTWARD: 'outward',
  TRANSFER: 'transfer',
  PROCESSING: 'processing',
  ADJUSTMENT: 'adjustment',
};

/** Generate a locally-unique temp id for offline transactions. */
export const makeTmpId = (type, godown) => {
  const g = (godown || 'unknown').slice(0, 4).toUpperCase();
  const t = type.slice(0, 2).toUpperCase();
  const stamp = Date.now().toString(36);
  const rnd = Math.random().toString(36).slice(2, 6);
  return `OFFLINE-${t}-${g}-${stamp}-${rnd}`;
};

export async function addOp({ type, payload, godown }) {
  const db = await getDB();
  const tmp_id = makeTmpId(type, godown);
  const op = {
    tmp_id,
    type,
    payload,
    godown: godown || payload?.godown || null,
    status: 'pending',
    created_at: new Date().toISOString(),
    last_error: null,
    attempts: 0,
  };
  await db.put(STORE_OPS, op);
  return op;
}

export async function listOps(statusFilter) {
  const db = await getDB();
  if (!statusFilter) return db.getAll(STORE_OPS);
  return db.getAllFromIndex(STORE_OPS, 'byStatus', statusFilter);
}

export async function removeOp(tmp_id) {
  const db = await getDB();
  return db.delete(STORE_OPS, tmp_id);
}

export async function markOpFailed(tmp_id, errMsg) {
  const db = await getDB();
  const op = await db.get(STORE_OPS, tmp_id);
  if (!op) return;
  op.status = 'failed';
  op.last_error = String(errMsg || 'Unknown error').slice(0, 500);
  op.attempts = (op.attempts || 0) + 1;
  await db.put(STORE_OPS, op);
}

export async function markOpSyncing(tmp_id) {
  const db = await getDB();
  const op = await db.get(STORE_OPS, tmp_id);
  if (!op) return;
  op.status = 'syncing';
  await db.put(STORE_OPS, op);
}

export async function resetOpToPending(tmp_id) {
  const db = await getDB();
  const op = await db.get(STORE_OPS, tmp_id);
  if (!op) return;
  op.status = 'pending';
  op.last_error = null;
  await db.put(STORE_OPS, op);
}

export async function countPending() {
  const db = await getDB();
  const pending = await db.getAllKeysFromIndex(STORE_OPS, 'byStatus', 'pending');
  const failed = await db.getAllKeysFromIndex(STORE_OPS, 'byStatus', 'failed');
  const syncing = await db.getAllKeysFromIndex(STORE_OPS, 'byStatus', 'syncing');
  return {
    pending: pending.length,
    failed: failed.length,
    syncing: syncing.length,
    total: pending.length + failed.length + syncing.length,
  };
}

/** Is an ID a client-generated temp ID? */
export const isTmpId = (id) => typeof id === 'string' && id.startsWith('OFFLINE-');
